/**
 * Schedules the next check of exclusive accesses based on their start and end times.
 *
 * This method calculates the closest future time (either a start time or an end time)
 * from the current time for all exclusive accesses. It then sets a timeout to wait
 * until this closest time. Once the timeout is reached, it updates the access statuses
 * and reschedules the next check. This approach is efficient as it avoids continuous
 * checking (e.g., every second) and instead waits precisely until an update is necessary.
 */
const scheduleNextCheck = (exclusiveAccesses, cb = null) => {
  const currentTime = new Date().getTime();
  let closestTime = Number.MAX_VALUE;

  if (exclusiveAccesses.exclusive_accesses) {
    // Iterate over each exclusive access to find the closest future time
    exclusiveAccesses.exclusive_accesses.forEach((access) => {
      const startTime = new Date(access.start_time).getTime();
      const endTime = new Date(access.end_time).getTime();

      // Check if the current time is before the start time
      if (currentTime < startTime) {
        // Update the closest time if this start time is nearer
        closestTime = Math.min(closestTime, startTime);
        access.next_check = access.start_time;
      } else {
        // Check if the current time is before the end time
        if (currentTime < endTime) {
          // Update the closest time if this end time is nearer
          closestTime = Math.min(closestTime, endTime);
          access.next_check = access.end_time;
        } else {
          closestTime = currentTime;
        }
      }

      access.delay = closestTime - currentTime;

      // Calculate the delay until the closest time
      const delay = closestTime - currentTime;

      // Set a timeout only if there is a valid future time
      if (delay > 0) {
        setTimeout(() => {
          scheduleNextCheck(exclusiveAccesses, cb);
          if (cb != null) {
            cb();
          }
        }, delay);
      }
    });
  }
};

/**
 * Compares the current time with a given time string.
 *
 * @param {string} timeString - The time string to compare with the current time, in ISO 8601 format.
 * @return {string} - Returns 'before' if the current time is before the given time,
 *                    'after' if the current time is after the given time,
 *                    and 'equal' if both times are equal.
 */
const compareWithCurrentTime = (timeString) => {
  // Convert the time string to a Date object
  const inputTime = new Date(timeString);

  // Get the current time as a Date object
  const currentTime = new Date();

  // Compare the two times
  if (inputTime < currentTime) {
    return 'before';
  } else if (inputTime > currentTime) {
    return 'after';
  } else {
    return 'equal';
  }
};

const updateAccessStatus = (access, customer) => {
  const startTimeComparison = compareWithCurrentTime(access.start_time);
  const endTimeComparison = compareWithCurrentTime(access.end_time);

  if (startTimeComparison === 'after' || endTimeComparison === 'before') {
    access.exclusive_access = true;
  } else if (customer == null) {
    access.exclusive_access = false;
  } else {
    if (access.allow_accounts) {
      access.exclusive_access = true;
    } else {
      access.exclusive_access =
        customer.exclusive_accesses.length > 0 &&
        customer.exclusive_accesses.some((a) => a.handle === access.handle);
    }
  }
  if (endTimeComparison === 'before') {
    access.status = 'expired';
  }

  if (startTimeComparison === 'before' && endTimeComparison === 'after') {
    access.status = 'active';
  }

  if (startTimeComparison === 'after' && endTimeComparison === 'after') {
    access.status = 'upcoming';
  }

  if (
    endTimeComparison === 'after' &&
    customer != null &&
    customer.exclusive_accesses.some((a) => a.handle === access.handle) &&
    startTimeComparison === 'after'
  ) {
    access.future_exclusive_access = true;
  } else {
    access.future_exclusive_access = false;
  }

  if (access.launch != null) {
    updateAccessStatus(access.launch, customer);
  }
};

const updateAccessesStatus = (exclusiveAccesses) => {
  const { exclusive_accesses, customer } = exclusiveAccesses;

  let noAccesses = [];
  const data = {
    hasAccess: true,
    bisForm: '',
    customButtonText: '',
  };

  if (exclusive_accesses && exclusive_accesses.length) {
    exclusive_accesses.forEach((access) =>
      updateAccessStatus(access, customer)
    );
    data.hasAccess = !exclusive_accesses.some(
      (a) => a.exclusive_access === false
    );

    if (!data.hasAccess) {
      noAccesses = exclusive_accesses.filter(
        (a) => a.exclusive_access === false
      );
      noAccesses.sort((a, b) => {
        let endTimeA = new Date(a.end_time);
        let endTimeB = new Date(b.end_time);

        return endTimeA - endTimeB;
      });
      if (noAccesses[0].launch != null) {
        noAccesses[0].launch.future_exclusive_access
          ? (data.bisForm = noAccesses[0].launch.bis_authorised)
          : (data.bisForm = noAccesses[0].launch.bis_unauthorised);
        data.customButtonText = noAccesses[0].launch.custom_button_text;
      } else {
        data.bisForm = noAccesses[0].bis_unauthorised;
        data.customButtonText = noAccesses[0].custom_button_text;
        document.querySelector('.product__submit__buttons .product__submit__add').classList.add('active');
        document.querySelector('.account_link_button').classList.add('active');
        document.querySelector('.account_link_button').innerHTML = noAccesses[0].custom_button_text;
      }
    }
  }
  if (exclusiveAccesses.product) {
    exclusiveAccesses.product.variants.map((variant) => {
      variant.exclusive_access = data.hasAccess;
    });
  }

  data.exclusive_accesses = exclusive_accesses;

  return data;
};

const validateEmail = (email) => {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
};

const handleBISForm = () => {
  document.querySelectorAll('.bis__modal--custom').forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target === modal.querySelector('.close')) {
        modal.classList.remove('is-active');
      }
    });

    modal.querySelector('#submit-btn').addEventListener('click', (e) => {
      const variantId = modal.querySelector('#variants').value;
      const productId = modal.querySelector('input[name="product"]').value;
      const $email = modal.querySelector('input[name="email"]');
      const $successMessage = modal.querySelector('.completed_message');
      const email = $email.value;

      if (!validateEmail(email)) {
        $email.setCustomValidity('Please enter a valid email address');
      } else {
        $email.setCustomValidity('');
      }
      $email.reportValidity();
      console.log('submit', variantId, email, productId);
      BIS.create(email, variantId, productId).then(function (resp) {
        if (resp.status == 'OK') {
          $successMessage.style.display = 'block';
          setTimeout(() => {
            modal.classList.remove('is-active');
          }, 3000);
        }
      });
    });
  });
};

// Wait for the window to load before executing the script
window.onload = function () {
  // Find all input elements with the attribute 'data-exclusive-access-remove-from-cart'
  const inputs = document.querySelectorAll(
    'input[data-exclusive-access-remove-from-cart]'
  );

  // Initialise an empty object to hold the updates
  let updates = {};

  if (inputs.length === 0) {
    return;
  }

  // Loop through each input and add its value to the 'updates' object
  inputs.forEach((input) => {
    updates[parseInt(input.value)] = 0; // Set to 0 for removal from cart
  });

  // Perform a POST request to update the cart
  fetch(window.Shopify.routes.root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates }), // Convert the 'updates' object to a JSON string
  })
    .then((response) => {
      // Convert the response to JSON
      return response.json();
    })
    .then((data) => {
      // Reload the page upon successful cart update
      window.location.reload();
    })
    .catch((error) => {
      // Log any errors encountered during the fetch operation
      console.error('Error:', error);
    });
};
